import { useCallback, useEffect, useState } from 'react';
import { useReferentialActions } from '../contexts/ReferentialContext';
import { referentialCacheService } from '../contexts/services/referentialCacheService';

interface CacheStatus {
  hasCache: boolean;
  cacheSize: number;
  cacheAge: number;
  version: string;
  isExpired: boolean;
}

export const useReferentialCache = () => {
  const [cacheStatus, setCacheStatus] = useState<CacheStatus>({
    hasCache: false,
    cacheSize: 0,
    cacheAge: 0,
    version: 'N/A',
    isExpired: false
  });

  const { clearCacheAsync, refreshReferentialsAsync } = useReferentialActions();

  const updateCacheStatus = useCallback(() => {
    const stats = referentialCacheService.getCacheStats();
    const isExpired = stats.cacheAge > 24 * 60 * 60 * 1000; // 24h

    setCacheStatus({
      hasCache: stats.hasCache,
      cacheSize: stats.cacheSize,
      cacheAge: stats.cacheAge,
      version: stats.version,
      isExpired
    });
  }, []);

  const clearCache = useCallback(async () => {
    await clearCacheAsync();
    updateCacheStatus();
  }, [clearCacheAsync, updateCacheStatus]);

  const refreshCache = useCallback(async () => {
    await refreshReferentialsAsync();
    updateCacheStatus();
  }, [refreshReferentialsAsync, updateCacheStatus]);

  const formatCacheSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }, []);

  const formatCacheAge = useCallback((milliseconds: number): string => {
    if (milliseconds === 0) return '0 min';
    const minutes = Math.floor(milliseconds / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}j ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}min`;
    return `${minutes}min`;
  }, []);

  useEffect(() => {
    updateCacheStatus();
    
    // Optionnel : surveiller les changements dans localStorage
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'referentials_cache') {
        updateCacheStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [updateCacheStatus]);

  return {
    cacheStatus,
    clearCache,
    refreshCache,
    updateCacheStatus,
    formatCacheSize,
    formatCacheAge,
    utils: {
      isCacheValid: () => referentialCacheService.isCacheValid(),
      getCacheStats: () => referentialCacheService.getCacheStats()
    }
  };
};

// Composant optionnel pour afficher le statut du cache (utile pour le debug/admin)
// components/ReferentialCacheStatus.tsx
